/**
 * Idle-detection threshold (minutes without input before the running timer is
 * considered idle) — client-side storage.
 *
 * Like the weekly goal and the billing rates, this is a private, device-scoped
 * preference stored in localStorage rather than the synced schema. The pure idle
 * math lives in `@time-tracker/shared` (`computeIdleState`/`resolveIdleAction`);
 * this module only reads and writes the stored threshold, SSR-safely, and exposes
 * it as a signal so the always-mounted IdleGuard reacts to Settings changes
 * without a reload.
 */

import { createSignal } from "solid-js";
import {
  clampIdleThresholdMinutes,
  IDLE_THRESHOLD_STORAGE_KEY,
  parseIdleThresholdMinutes,
} from "@time-tracker/shared";

function readStoredIdleThresholdMinutes(): number {
  if (typeof localStorage === "undefined") {
    return 0;
  }
  return parseIdleThresholdMinutes(localStorage.getItem(IDLE_THRESHOLD_STORAGE_KEY));
}

const [idleThresholdSignal, setIdleThresholdSignal] = createSignal(
  readStoredIdleThresholdMinutes(),
);

/** Reactive accessor: current idle threshold in minutes (0 = detection off). */
export const idleThresholdMinutes = idleThresholdSignal;

/** Reads the stored idle threshold in minutes (0 = detection off). */
export function getIdleThresholdMinutes(): number {
  return readStoredIdleThresholdMinutes();
}

/** Persists the idle threshold in minutes (clamped) and notifies subscribers. */
export function setIdleThresholdMinutes(minutes: number): void {
  const clamped = clampIdleThresholdMinutes(minutes);
  if (typeof localStorage !== "undefined") {
    localStorage.setItem(IDLE_THRESHOLD_STORAGE_KEY, String(clamped));
  }
  setIdleThresholdSignal(clamped);
}
